import { Calendar } from '@fullcalendar/core';
import dayGridPlugin from '@fullcalendar/daygrid';
import timeGridPlugin from '@fullcalendar/timegrid';
import listPlugin from '@fullcalendar/list';
import interactionPlugin from '@fullcalendar/interaction';
import { format, parseISO, isValid } from 'date-fns';

document.addEventListener('DOMContentLoaded', function() {
    // Parse query string parameters
    const urlParams = new URLSearchParams(window.location.search);
    const signupsParam = urlParams.get('signups');
    const monthParam = urlParams.get('month');
    const showFullParam = urlParams.get('showfull');
    const viewParam = urlParams.get('view');
    
    let requestedSignupIds = null;
    let requestedMonth = null;
    let requestedShowFull = false;
    let requestedView = 'month'; // default view
    
    if (signupsParam) {
        // Parse comma-separated list of 1-based IDs
        requestedSignupIds = signupsParam.split(',')
            .map(id => parseInt(id.trim()))
            .filter(id => !isNaN(id) && id > 0);
    }
    
    if (monthParam) {
        // Parse YYYY-MM format
        const monthMatch = monthParam.match(/^(\d{4})-(\d{1,2})$/);
        if (monthMatch) {
            const year = parseInt(monthMatch[1]);
            const month = parseInt(monthMatch[2]);
            if (year >= 2020 && year <= 2030 && month >= 1 && month <= 12) {
                requestedMonth = new Date(year, month - 1, 1); // Convert to 0-based month
            }
        }
    }
    
    if (showFullParam === '1') {
        requestedShowFull = true;
    }
    
    if (viewParam) {
        const validViews = ['month', 'list', 'week', 'day'];
        if (validViews.includes(viewParam.toLowerCase())) {
            requestedView = viewParam.toLowerCase();
        }
    }
    
    // Date-fns functions
    const formatDate = format;
    
    // Global variables for filtering
    let allEvents = [];
    let signupSources = [];
    let activeFilters = new Set();
    let showFullTimeslots = requestedShowFull;
    let toggleInitialized = false;
    
    // Cache configuration
    const CACHE_EXPIRY_HOURS = 1;
    const CACHE_KEY_PREFIX = 'csg_calendar_cache_';
    const calendarEl = document.getElementById('calendar');
    const loadingEl = document.getElementById('loading');
    const modal = document.getElementById('eventModal');
    const modalTitle = document.getElementById('modalTitle');
    const modalBody = document.getElementById('modalBody');
    const closeBtn = document.getElementsByClassName('close')[0];
    
    // Map view parameter to FullCalendar view names
    const getFullCalendarView = (viewType) => {
        switch (viewType) {
            case 'month': return 'dayGridMonth';
            case 'week': return 'timeGridWeek';
            case 'day': return 'timeGridDay';
            case 'list': return 'listMonth';
            default: return 'dayGridMonth';
        }
    };

    // Helper to get simplified view name from FullCalendar view
    const getSimpleViewName = (fcViewType) => {
        switch (fcViewType) {
            case 'dayGridMonth': return 'month';
            case 'timeGridWeek': return 'week';
            case 'timeGridDay': return 'day';
            case 'listMonth': return 'list';
            default: return 'month';
        }
    };

    // Helper to parse time string and combine with date
    const parseTimeToDate = (date, timeString) => {
        if (!timeString || !date) return date;
        
        // Extract time from strings like "3p", "3:30p", "10a", "10:30a"
        const timeMatch = timeString.match(/(\d{1,2})(?::(\d{2}))?\s*(a|p|am|pm)?/i);
        if (!timeMatch) return date;
        
        let hours = parseInt(timeMatch[1]);
        const minutes = parseInt(timeMatch[2] || '0');
        const ampm = timeMatch[3] ? timeMatch[3].toLowerCase() : '';
        
        // Convert to 24-hour format
        if (ampm.startsWith('p') && hours !== 12) {
            hours += 12;
        } else if (ampm.startsWith('a') && hours === 12) {
            hours = 0;
        }
        
        // Create new date with the time
        const result = new Date(date);
        result.setHours(hours, minutes, 0, 0);
        
        return result;
    };

    // Helper to format datetime as local string for FullCalendar
    const formatDateTimeLocal = (date) => {
        const year = date.getFullYear();
        const month = String(date.getMonth() + 1).padStart(2, '0');
        const day = String(date.getDate()).padStart(2, '0');
        const hours = String(date.getHours()).padStart(2, '0');
        const minutes = String(date.getMinutes()).padStart(2, '0');
        const seconds = String(date.getSeconds()).padStart(2, '0');
        
        return `${year}-${month}-${day}T${hours}:${minutes}:${seconds}`;
    };

    // Cache utility functions
    const getCacheKey = (month, year, signups = '') => {
        return `${CACHE_KEY_PREFIX}${year}_${month}${signups ? '_' + signups : ''}`;
    };

    const getCachedData = (cacheKey) => {
        try {
            const cached = localStorage.getItem(cacheKey);
            if (!cached) return null;
            
            const data = JSON.parse(cached);
            const now = new Date().getTime();
            const cacheAge = now - data.timestamp;
            const maxAge = CACHE_EXPIRY_HOURS * 60 * 60 * 1000; // Convert to milliseconds
            
            if (cacheAge > maxAge) {
                localStorage.removeItem(cacheKey);
                return null;
            }
            
            return data.payload;
        } catch (error) {
            return null;
        }
    };

    const setCachedData = (cacheKey, data) => {
        try {
            const cacheData = {
                timestamp: new Date().getTime(),
                payload: data
            };
            localStorage.setItem(cacheKey, JSON.stringify(cacheData));
            updateLastRefreshDisplay();
        } catch (error) {
            // Silently fail if localStorage is full or unavailable
        }
    };

    const getRelativeTime = (timestamp) => {
        const now = new Date().getTime();
        const diff = now - timestamp;
        const minutes = Math.floor(diff / (1000 * 60));
        const hours = Math.floor(diff / (1000 * 60 * 60));
        
        if (minutes < 1) return 'just now';
        if (minutes === 1) return '1 minute ago';
        if (minutes < 60) return `${minutes} minutes ago`;
        if (hours === 1) return '1 hour ago';
        if (hours < 24) return `${hours} hours ago`;
        return 'more than a day ago';
    };

    const updateLastRefreshDisplay = () => {
        const lastRefreshEl = document.getElementById('last-refresh');
        if (!lastRefreshEl) return;
        
        // Get the most recent cache timestamp
        let latestTimestamp = 0;
        for (let i = 0; i < localStorage.length; i++) {
            const key = localStorage.key(i);
            if (key && key.startsWith(CACHE_KEY_PREFIX)) {
                try {
                    const cached = JSON.parse(localStorage.getItem(key));
                    if (cached.timestamp > latestTimestamp) {
                        latestTimestamp = cached.timestamp;
                    }
                } catch (error) {
                    // Skip invalid entries
                }
            }
        }
        
        if (latestTimestamp > 0) {
            lastRefreshEl.textContent = `Updated ${getRelativeTime(latestTimestamp)}`;
        } else {
            lastRefreshEl.textContent = '';
        }
    };

    // Helper function to add an event to the calendar
    const addEventToCalendar = (event) => {
        try {
            // Ensure event has required properties
            if (!event.start) return;
            if (!event.title) event.title = 'Volunteer Opportunity';
            
            // Create proper datetime events if we have time information
            const props = event.extendedProps || {};
            const startTime = props.startTime;
            const endTime = props.endTime;
            
            let eventStart, eventEnd, allDay;
            
            if (startTime) {
                // Parse the event date properly to avoid timezone issues
                let eventDate;
                if (typeof event.start === 'string') {
                    // Parse date string without timezone conversion
                    const dateParts = event.start.split('-');
                    eventDate = new Date(parseInt(dateParts[0]), parseInt(dateParts[1]) - 1, parseInt(dateParts[2]));
                } else {
                    eventDate = event.start;
                }
                
                // Create start datetime
                const startDateTime = parseTimeToDate(eventDate, startTime);
                // Format as local datetime string for FullCalendar
                eventStart = formatDateTimeLocal(startDateTime);
                allDay = false;
                
                // Create end datetime if we have end time
                if (endTime) {
                    const endDateTime = parseTimeToDate(eventDate, endTime);
                    eventEnd = formatDateTimeLocal(endDateTime);
                } else {
                    // Default to 1 hour duration if no end time
                    const defaultEnd = new Date(startDateTime);
                    defaultEnd.setHours(defaultEnd.getHours() + 1);
                    eventEnd = formatDateTimeLocal(defaultEnd);
                }
            } else {
                // Fallback to all-day event
                eventStart = typeof event.start === 'string' ? event.start : event.start.toISOString().split('T')[0];
                allDay = true;
            }
            
            const calendarEvent = {
                id: event.id,
                title: event.title,
                start: eventStart,
                allDay: allDay,
                backgroundColor: '#C8102E',  // Force CSG red
                borderColor: '#C8102E',     // Force CSG red border
                extendedProps: event.extendedProps
            };
            
            // Add end time if available
            if (eventEnd) {
                calendarEvent.end = eventEnd;
            }
            
            calendar.addEvent(calendarEvent);
            return true;
        } catch (error) {
            return false;
        }
    };

    // Initialize FullCalendar
    const calendar = new Calendar(calendarEl, {
        plugins: [dayGridPlugin, timeGridPlugin, listPlugin, interactionPlugin],
        initialView: getFullCalendarView(requestedView),
        initialDate: requestedMonth || undefined, // Set initial month if provided
        headerToolbar: {
            left: 'prev,next today',
            center: 'title',
            right: 'dayGridMonth,timeGridWeek,timeGridDay,listMonth'
        },
        height: 'auto',
        aspectRatio: 1.6,
        eventDisplay: 'block',
        displayEventTime: true, // Show times in time-based views
        displayEventEnd: false,
        dayMaxEvents: false, // Allow unlimited events to show
        moreLinkClick: 'popover',
        eventMinHeight: 20, // Minimum height for events
        dayMaxEventRows: 6, // Allow more rows of events
        slotMinTime: '06:00:00', // Start calendar at 6 AM
        slotMaxTime: '22:00:00', // End calendar at 10 PM
        allDaySlot: true, // Show all-day slot
        
        // Event handling
        eventClick: function(info) {
            showEventDetails(info.event);
        },
        
        // Custom event rendering
        eventDidMount: function(info) {
            const props = info.event.extendedProps;
            const spotsAvailable = props.spotsAvailable || 0;
            const startTime = props.startTime || '';
            const endTime = props.endTime || '';
            
            // Only apply custom rendering for non-list views
            if (info.view.type !== 'listMonth') {
                // Create custom HTML structure for grid views
                const timeDisplay = startTime && endTime ? `${startTime}-${endTime}` : startTime;
                const slotsText = spotsAvailable > 0 ? `${spotsAvailable} slots` : 'Full';
                
                // Clean the title by removing time prefix if it exists
                let cleanTitle = info.event.title;
                if (timeDisplay && cleanTitle.startsWith(timeDisplay)) {
                    cleanTitle = cleanTitle.substring(timeDisplay.length).trim();
                }
                // Also handle cases where title starts with just start time
                if (startTime && cleanTitle.startsWith(startTime)) {
                    cleanTitle = cleanTitle.substring(startTime.length).trim();
                }
                
                // Replace the event content with custom layout
                const eventContent = `
                    <div class="event-header">
                        <span class="event-time">${timeDisplay}</span>
                        <span class="event-slots ${spotsAvailable > 0 ? 'available' : 'full'}">${slotsText}</span>
                    </div>
                    <div class="event-title-wrap">
                        <div class="event-title-text">${cleanTitle}</div>
                    </div>
                `;
                
                // Clear all existing content and replace with custom content
                info.el.innerHTML = '';
                info.el.insertAdjacentHTML('afterbegin', eventContent);
                
                // Add tooltip
                const spotsText = spotsAvailable > 0 
                    ? `${spotsAvailable} spots available`
                    : 'Full';
                
                info.el.title = `${info.event.title}\n${timeDisplay}\n${spotsText}`;
                
                // Add custom class for full events
                if (spotsAvailable === 0) {
                    info.el.classList.add('event-full');
                }
            } else {
                // For list view, customize the title to include availability info
                const timeDisplay = startTime && endTime ? `${startTime}-${endTime}` : startTime;
                let cleanTitle = info.event.title;
                if (timeDisplay && cleanTitle.startsWith(timeDisplay)) {
                    cleanTitle = cleanTitle.substring(timeDisplay.length).trim();
                }
                if (startTime && cleanTitle.startsWith(startTime)) {
                    cleanTitle = cleanTitle.substring(startTime.length).trim();
                }
                
                const availabilityText = spotsAvailable > 0 
                    ? `(${spotsAvailable} spots available)`
                    : '(Full)';
                
                // Update the title element in list view
                const titleElement = info.el.querySelector('.fc-list-event-title');
                if (titleElement) {
                    titleElement.innerHTML = `${cleanTitle} <span style="color: var(--csg-gray); font-weight: 500;">${availabilityText}</span>`;
                }
                
                // Update the time element in list view to show only our clean format
                const timeElement = info.el.querySelector('.fc-list-event-time');
                if (timeElement) {
                    if (timeDisplay) {
                        timeElement.textContent = timeDisplay;
                    } else {
                        timeElement.textContent = 'All Day';
                    }
                }
                
                // Add visual indicator for full events
                if (spotsAvailable === 0) {
                    info.el.style.opacity = '0.6';
                }
            }
        },
        
        // Load events when view changes
        datesSet: function(dateInfo) {
            // Prevent multiple rapid calls
            if (window.loadingEvents) {
                return;
            }
            
            // Only load for the primary month that the calendar is focused on
            // Use the 15th of the month to ensure we're in the right month
            const calendarDate = calendar.getDate();
            const primaryDate = new Date(calendarDate.getFullYear(), calendarDate.getMonth(), 15);
            
            // Update URL when month changes
            updateURL();
            
            loadEvents(primaryDate);
        },
        
        // Track view changes
        viewDidMount: function(info) {
            // Update URL when view changes
            updateURL();
        },
        
        // Handle day clicks to navigate to day view
        dateClick: function(info) {
            // Only navigate to day view if we're not already in day view
            if (calendar.view.type !== 'timeGridDay') {
                // Change to day view and go to the clicked date
                calendar.changeView('timeGridDay', info.date);
                
                // URL will be updated automatically via viewDidMount and datesSet
            }
        }
    });
    
    calendar.render();
    
    // Initialize toggle immediately
    initializeToggle();
    
    // Initialize and update the last refresh display
    updateLastRefreshDisplay();
    
    // Update the relative time display every minute
    setInterval(updateLastRefreshDisplay, 60000);
    
    // Add refresh button functionality
    const refreshBtn = document.getElementById('refresh-btn');
    refreshBtn.addEventListener('click', () => {
        window.loadingEvents = false; // Reset the loading flag
        loadEvents(calendar.getDate(), true); // Force refresh
    });
    
    
    // Ensure events load - fallback if datesSet doesn't fire properly
    setTimeout(() => {
        if (!window.loadingEvents && calendar.getEvents().length === 0) {
            loadEvents(new Date());
        }
    }, 1000);
    
    // Track the last successful load to prevent stale error messages
    let lastLoadedMonth = null;
    
    // Function to load events from the API
    async function loadEvents(date, forceRefresh = false) {
        // Prevent concurrent loads
        if (window.loadingEvents) {
            return;
        }
        
        window.loadingEvents = true;
        showLoading(true);
        hideError(); // Hide any previous error messages
        
        // Auto-reset loading flag after 10 seconds to prevent deadlock
        const timeoutId = setTimeout(() => {
            window.loadingEvents = false;
        }, 10000);
        
        // Use date-fns for more reliable date formatting
        const month = formatDate(date, 'M'); // Month without leading zero
        const year = formatDate(date, 'yyyy');
        const signupsKey = requestedSignupIds ? requestedSignupIds.join(',') : '';
        const cacheKey = getCacheKey(month, year, signupsKey);
        
        // Check cache first (unless forcing refresh)
        if (!forceRefresh) {
            const cachedData = getCachedData(cacheKey);
            if (cachedData) {
                // Use cached data
                allEvents = cachedData.events || [];
                signupSources = cachedData.sources || [];
                
                // Initialize filters if this is the first load
                if (activeFilters.size === 0) {
                    if (requestedSignupIds && requestedSignupIds.length > 0) {
                        signupSources.forEach((source, index) => {
                            const sourceNumber = index + 1;
                            if (requestedSignupIds.includes(sourceNumber)) {
                                activeFilters.add(source.id);
                            }
                        });
                    } else {
                        signupSources.forEach(source => activeFilters.add(source.id));
                    }
                    buildFilterUI(signupSources);
                    initializeToggle();
                }
                
                // Clear and add events
                calendar.removeAllEvents();
                calendar.removeAllEventSources();
                const filteredEvents = filterEvents(allEvents, activeFilters);
                
                filteredEvents.forEach((event) => {
                    addEventToCalendar(event);
                });
                
                calendar.render();
                showLoading(false);
                window.loadingEvents = false;
                clearTimeout(timeoutId);
                updateLastRefreshDisplay();
                return;
            }
        }
        
        try {
            // In production, this will call the Netlify function
            // For local development, you might want to use a proxy
            const baseUrl = window.location.hostname === 'localhost' 
                ? 'http://localhost:8888/.netlify/functions'
                : '/.netlify/functions';
            
            let url = `${baseUrl}/get-signups?month=${month}&year=${year}`;
            
            // Add signups parameter if specified
            if (requestedSignupIds && requestedSignupIds.length > 0) {
                url += `&signups=${requestedSignupIds.join(',')}`;
            }
            
            const response = await fetch(url);
            
            if (!response.ok) {
                throw new Error(`HTTP error! status: ${response.status}`);
            }
            
            const data = await response.json();
            
            // Handle the response format  
            let events;
            let sources;
            if (data.events && Array.isArray(data.events)) {
                events = data.events;
                sources = data.sources || [];
            } else if (Array.isArray(data)) {
                events = data;
                sources = [];
            } else {
                events = [];
                sources = [];
            }
            
            // Store data globally for filtering
            allEvents = events;
            signupSources = sources;
            
            // Cache the successful API response
            setCachedData(cacheKey, { events, sources });
            
            // Initialize filters if this is the first load
            if (activeFilters.size === 0) {
                // If signups parameter was provided, only activate those filters
                if (requestedSignupIds && requestedSignupIds.length > 0) {
                    sources.forEach((source, index) => {
                        // Convert 1-based ID from URL to match against source index
                        const sourceNumber = index + 1;
                        if (requestedSignupIds.includes(sourceNumber)) {
                            activeFilters.add(source.id);
                        }
                    });
                } else {
                    // Default behavior: activate all filters
                    sources.forEach(source => activeFilters.add(source.id));
                }
                buildFilterUI(sources);
                initializeToggle();
            }
            
            // Clear existing events and add new ones
            calendar.removeAllEvents();
            calendar.removeAllEventSources();
            
            // Filter events based on active filters
            const filteredEvents = filterEvents(events, activeFilters);
            
            // Process the events and determine final outcome
            let finalResult = { success: false, message: '', eventCount: 0 };
            
            if (Array.isArray(filteredEvents) && filteredEvents.length > 0) {
                // Add events to calendar
                let successCount = 0;
                filteredEvents.forEach((event) => {
                    if (addEventToCalendar(event)) {
                        successCount++;
                    }
                });
                
                // Force calendar refresh
                calendar.render();
                
                if (successCount === 0) {
                    finalResult = { 
                        success: false, 
                        message: 'Events were received but could not be displayed on the calendar.',
                        eventCount: 0
                    };
                } else {
                    finalResult = { 
                        success: true, 
                        message: '',
                        eventCount: successCount
                    };
                }
            } else {
                if (!Array.isArray(events)) {
                    finalResult = { 
                        success: false, 
                        message: 'Invalid data format received from server.',
                        eventCount: 0
                    };
                } else {
                    // Empty array - no events found
                    finalResult = { 
                        success: false, 
                        message: 'No volunteer opportunities are currently available for this month. New opportunities are added regularly - please check back soon!',
                        eventCount: 0
                    };
                }
            }
            
            // Only show error after all processing is complete
            if (!finalResult.success && finalResult.eventCount === 0) {
                // Add a small delay to ensure loading animation completes
                setTimeout(() => {
                    showError(finalResult.message);
                }, 100);
            }
            
        } catch (error) {
            setTimeout(() => {
                showError(`We're having trouble connecting right now. Please try refreshing the page or check back later. ${error.message}`);
            }, 100);
        } finally {
            clearTimeout(timeoutId); // Clear the timeout since we're done
            showLoading(false);
            window.loadingEvents = false;
        }
    }
    
    // Show event details in modal
    function showEventDetails(event) {
        const props = event.extendedProps;
        
        modalTitle.textContent = event.title;
        
        let detailsHTML = '';
        
        // Date and time using date-fns
        let eventDate;
        let dateStr;
        
        try {
            if (typeof event.start === 'string') {
                eventDate = parseISO(event.start);
            } else {
                eventDate = event.start; // Already a Date object from FullCalendar
            }
            
            if (isValid(eventDate)) {
                dateStr = formatDate(eventDate, 'EEEE, MMMM do, yyyy');
            } else {
                dateStr = 'Date not available';
            }
        } catch (error) {
            console.error('Error formatting date:', error);
            dateStr = 'Date not available';
        }
        
        detailsHTML += `
            <div class="event-detail">
                <span class="event-label">Date:</span>
                ${dateStr}
            </div>
        `;
        
        // Time if not all day
        if (!event.allDay && props.startTime) {
            detailsHTML += `
                <div class="event-detail">
                    <span class="event-label">Time:</span>
                    ${props.startTime}${props.endTime ? ' - ' + props.endTime : ''}
                </div>
            `;
        }
        
        // Location
        if (props.location) {
            detailsHTML += `
                <div class="event-detail">
                    <span class="event-label">Location:</span>
                    ${props.location}
                </div>
            `;
        }
        
        // Description
        if (props.description) {
            detailsHTML += `
                <div class="event-detail">
                    <span class="event-label">Description:</span>
                    ${props.description}
                </div>
            `;
        }
        
        // Availability
        const availabilityClass = props.spotsAvailable > 0 ? 'available' : 'full';
        const availabilityText = props.spotsAvailable > 0 
            ? `${props.spotsAvailable} of ${props.totalSpots} spots available`
            : `This opportunity is full (${props.signedUp} volunteers joined)`;
        
        detailsHTML += `
            <div class="event-detail ${availabilityClass}">
                <span class="event-label">Availability:</span>
                ${availabilityText}
            </div>
        `;
        
        // Sign up button (if spots available)
        if (props.spotsAvailable > 0) {
            const signupUrl = 'https://www.signupgenius.com/go/10C084BADAA2BA2FFC43-58367505-unicorner';
            detailsHTML += `
                <div style="margin-top: 20px; text-align: center;">
                    <a href="${signupUrl}" 
                       target="_blank" 
                       class="btn btn-primary"
                       style="display: inline-block; padding: 12px 24px; background-color: var(--csg-gold); color: white; text-decoration: none; border-radius: 5px; transition: all 0.3s ease; font-family: 'Source Sans Pro', sans-serif; font-weight: 600;">
                        Join Our Community
                    </a>
                    <p style="margin-top: 8px; font-size: 0.9em; color: var(--csg-gray);">Make a difference in girls' education</p>
                </div>
            `;
        }
        
        modalBody.innerHTML = detailsHTML;
        modal.classList.add('show');
        modal.setAttribute('aria-hidden', 'false');
    }
    
    // Close modal
    closeBtn.onclick = function() {
        modal.classList.remove('show');
        modal.setAttribute('aria-hidden', 'true');
    };
    
    window.onclick = function(event) {
        if (event.target == modal) {
            modal.classList.remove('show');
            modal.setAttribute('aria-hidden', 'true');
        }
    };
    
    // Show/hide loading spinner
    function showLoading(show) {
        if (show) {
            loadingEl.classList.add('active');
        } else {
            loadingEl.classList.remove('active');
        }
    }
    
    // Show error message
    function showError(message) {
        modalTitle.textContent = 'Error';
        modalBody.innerHTML = `
            <div class="event-detail" style="color: var(--csg-navy);">
                ${message}
            </div>
        `;
        modal.classList.add('show');
    }
    
    // Hide error message
    function hideError() {
        modal.classList.remove('show');
        modal.setAttribute('aria-hidden', 'true');
    }
    
    // Add keyboard navigation
    document.addEventListener('keydown', function(e) {
        if (e.key === 'Escape' && modal.classList.contains('show')) {
            modal.classList.remove('show');
            modal.setAttribute('aria-hidden', 'true');
        }
    });
    
    // Filtering functions
    function buildFilterUI(sources) {
        const filterContainer = document.getElementById('filter-checkboxes');
        filterContainer.innerHTML = '';
        
        sources.forEach((source, index) => {
            const filterItem = document.createElement('div');
            filterItem.className = 'filter-item';
            
            const checkbox = document.createElement('input');
            checkbox.type = 'checkbox';
            checkbox.id = `filter-${source.id}`;
            checkbox.className = 'filter-checkbox';
            
            // Check if this filter should be active based on URL parameter or default
            if (requestedSignupIds && requestedSignupIds.length > 0) {
                const sourceNumber = index + 1;
                checkbox.checked = requestedSignupIds.includes(sourceNumber);
            } else {
                checkbox.checked = activeFilters.has(source.id);
            }
            
            checkbox.addEventListener('change', () => handleFilterChange(source.id, checkbox.checked));
            
            const label = document.createElement('label');
            label.htmlFor = `filter-${source.id}`;
            label.className = 'filter-label';
            label.textContent = source.name;
            
            const count = document.createElement('span');
            count.className = 'filter-count';
            count.textContent = source.eventCount;
            
            filterItem.appendChild(checkbox);
            filterItem.appendChild(label);
            filterItem.appendChild(count);
            
            filterContainer.appendChild(filterItem);
        });
    }
    
    function handleFilterChange(sourceId, isChecked) {
        if (isChecked) {
            activeFilters.add(sourceId);
        } else {
            activeFilters.delete(sourceId);
        }
        
        // Re-filter and update calendar
        applyFilters();
        updateURL();
    }
    
    function filterEvents(events, activeFilters) {
        return events.filter(event => {
            const sourceId = event.extendedProps?.sourceId;
            const spotsAvailable = event.extendedProps?.spotsAvailable || 0;
            
            // Check if source is active
            const sourceActive = activeFilters.has(sourceId);
            
            // Check if we should show this event based on full timeslots setting
            const shouldShow = showFullTimeslots ? true : spotsAvailable > 0;
            
            return sourceActive && shouldShow;
        });
    }
    
    function applyFilters() {
        // Clear calendar
        calendar.removeAllEvents();
        
        // Filter events
        const filteredEvents = filterEvents(allEvents, activeFilters);
        
        // Add filtered events to calendar
        filteredEvents.forEach(event => {
            addEventToCalendar(event);
        });
        
        // Force calendar refresh
        calendar.render();
    }
    
    function initializeToggle() {
        if (toggleInitialized) {
            return;
        }
        
        const toggleInput = document.getElementById('show-full-toggle');
        const toggleSlider = document.querySelector('.toggle-slider');
        
        if (toggleInput) {
            // Set initial state based on URL parameter
            toggleInput.checked = showFullTimeslots;
            
            // Add event listener to the input (for programmatic changes)
            toggleInput.addEventListener('change', handleToggleChange);
            
            // Add click handler to the slider (since the input is invisible)
            if (toggleSlider) {
                toggleSlider.addEventListener('click', function(e) {
                    toggleInput.checked = !toggleInput.checked;
                    
                    // Trigger change event manually
                    const changeEvent = new Event('change', { bubbles: true });
                    toggleInput.dispatchEvent(changeEvent);
                });
            }
            
            // Also make the label clickable
            const toggleLabel = document.querySelector('.toggle-label');
            if (toggleLabel) {
                toggleLabel.addEventListener('click', function(e) {
                    toggleInput.checked = !toggleInput.checked;
                    
                    // Trigger change event manually
                    const changeEvent = new Event('change', { bubbles: true });
                    toggleInput.dispatchEvent(changeEvent);
                });
            }
            
            toggleInitialized = true;
        }
    }
    
    function handleToggleChange(event) {
        showFullTimeslots = event.target.checked;
        applyFilters();
        updateURL();
    }
    
    // URL management functions
    function updateURL() {
        const params = new URLSearchParams();
        const currentDate = calendar.getDate();
        const currentMonth = new Date();
        const currentView = calendar.view.type;
        
        // Add signups parameter if not all are selected
        if (signupSources.length > 0) {
            const allSelected = signupSources.every(source => activeFilters.has(source.id));
            if (!allSelected) {
                const selectedIndexes = signupSources
                    .map((source, index) => activeFilters.has(source.id) ? index + 1 : null)
                    .filter(index => index !== null);
                if (selectedIndexes.length > 0) {
                    params.set('signups', selectedIndexes.join(','));
                }
            }
        }
        
        // Add month parameter if not current month
        const isCurrentMonth = currentDate.getFullYear() === currentMonth.getFullYear() && 
                              currentDate.getMonth() === currentMonth.getMonth();
        if (!isCurrentMonth) {
            const monthStr = `${currentDate.getFullYear()}-${String(currentDate.getMonth() + 1).padStart(2, '0')}`;
            params.set('month', monthStr);
        }
        
        // Add view parameter if not default month view
        const simpleViewName = getSimpleViewName(currentView);
        if (simpleViewName !== 'month') {
            params.set('view', simpleViewName);
        }
        
        // Add showfull parameter if enabled
        if (showFullTimeslots) {
            params.set('showfull', '1');
        }
        
        // Update URL without refreshing the page
        const newUrl = params.toString() ? `${window.location.pathname}?${params.toString()}` : window.location.pathname;
        window.history.replaceState({}, '', newUrl);
    }
    
    function getCurrentDefaults() {
        const currentMonth = new Date();
        return {
            isCurrentMonth: (date) => {
                return date.getFullYear() === currentMonth.getFullYear() &&
                       date.getMonth() === currentMonth.getMonth();
            },
            allSignupsSelected: () => {
                return signupSources.length === 0 || signupSources.every(source => activeFilters.has(source.id));
            },
            showFullHidden: () => !showFullTimeslots
        };
    }

    // Build subscription URLs based on current filter state
    function buildSubscribeURL() {
        const baseUrl = window.location.origin + '/.netlify/functions/calendar';
        const params = new URLSearchParams();

        // Add active filters if not all are selected
        if (signupSources.length > 0) {
            const allSelected = signupSources.every(source => activeFilters.has(source.id));
            if (!allSelected) {
                const selectedIndexes = signupSources
                    .map((source, index) => activeFilters.has(source.id) ? index + 1 : null)
                    .filter(index => index !== null);
                if (selectedIndexes.length > 0) {
                    params.set('signups', selectedIndexes.join(','));
                }
            }
        }

        // Add showfull parameter if NOT showing full timeslots
        if (!showFullTimeslots) {
            params.set('showfull', '0');
        }

        // Default to 6 months
        params.set('months', '6');

        const queryString = params.toString();
        const httpsUrl = queryString ? `${baseUrl}?${queryString}` : baseUrl;
        const webcalUrl = httpsUrl.replace(/^https?:\/\//, 'webcal://');

        return { httpsUrl, webcalUrl };
    }

    // Subscribe modal elements
    const subscribeModal = document.getElementById('subscribeModal');
    const subscribeLink = document.getElementById('subscribe-link');
    const webcalUrlInput = document.getElementById('webcal-url');
    const httpsUrlInput = document.getElementById('https-url');
    const copyWebcalBtn = document.getElementById('copy-webcal');
    const copyHttpsBtn = document.getElementById('copy-https');

    // Open subscribe modal
    if (subscribeLink) {
        subscribeLink.addEventListener('click', function(e) {
            e.preventDefault();
            const urls = buildSubscribeURL();
            webcalUrlInput.value = urls.webcalUrl;
            httpsUrlInput.value = urls.httpsUrl;
            subscribeModal.style.display = 'block';
        });
    }

    // Copy to clipboard functionality
    function copyToClipboard(text, button) {
        navigator.clipboard.writeText(text).then(() => {
            const originalText = button.textContent;
            button.textContent = 'Copied!';
            button.classList.add('copied');
            setTimeout(() => {
                button.textContent = originalText;
                button.classList.remove('copied');
            }, 2000);
        }).catch(err => {
            console.error('Failed to copy:', err);
            alert('Failed to copy URL. Please copy manually.');
        });
    }

    if (copyWebcalBtn) {
        copyWebcalBtn.addEventListener('click', function() {
            copyToClipboard(webcalUrlInput.value, this);
        });
    }

    if (copyHttpsBtn) {
        copyHttpsBtn.addEventListener('click', function() {
            copyToClipboard(httpsUrlInput.value, this);
        });
    }

    // Make webcal URL clickable (opens in calendar app)
    if (webcalUrlInput) {
        webcalUrlInput.addEventListener('click', function() {
            window.location.href = this.value;
        });
        webcalUrlInput.style.cursor = 'pointer';
    }

    // Close subscribe modal
    const subscribeCloseBtn = subscribeModal?.querySelector('.close');
    if (subscribeCloseBtn) {
        subscribeCloseBtn.addEventListener('click', function() {
            subscribeModal.style.display = 'none';
        });
    }

    // Close modals when clicking outside
    window.addEventListener('click', function(event) {
        if (event.target === subscribeModal) {
            subscribeModal.style.display = 'none';
        }
    });
});